/**
 * Version releases. Structural facts (version, date, optional linked post) live
 * here and are shared across languages; the translatable parts — title, summary,
 * highlights — live per language in `i18n/locales/<lang>/releases.ts`, keyed by
 * version. The marquee on /blog links each version to its own auto-generated
 * release page at `/blog/release/<version>`.
 */
import type { Locale } from "../i18n/config";
import { dictionaries } from "../i18n/dictionaries";
import type { LocalizedRelease } from "../i18n/types";

export type ReleaseLevel = "major" | "minor" | "patch";

export interface ReleaseBase {
  /** Semver `X.Y.Z` — also the release page slug (`/blog/release/X.Y.Z`). */
  version: string;
  /** ISO `YYYY-MM-DD`. */
  date: string;
  /** Optional slug of a full blog post that goes deeper on this release. */
  postSlug?: string;
}

/** A release base merged with its translation for a specific locale. */
export interface Release extends ReleaseBase, LocalizedRelease {}

/**
 * Derive the update level from the version:
 *   X.0.0 → major   (most visible)
 *   x.Y.0 → minor   (second)
 *   x.y.Z → patch   (third)
 */
export function classifyRelease(version: string): ReleaseLevel {
  const [, minor = "0", patch = "0"] = version.split(".");
  if (Number(minor) === 0 && Number(patch) === 0) return "major";
  if (Number(patch) === 0) return "minor";
  return "patch";
}

/** Per-level presentation tokens (color + prominence). Labels come from the dictionary. */
export const LEVEL_STYLE: Record<ReleaseLevel, { color: string; weight: number; opacity: number }> = {
  major: { color: "#5ba8f5", weight: 700, opacity: 1 },
  minor: { color: "#4dd964", weight: 600, opacity: 0.92 },
  patch: { color: "#a8a49e", weight: 500, opacity: 0.72 },
};

// Newest first. Structural data only — see per-locale `releases.ts` for copy.
const releaseBases: ReleaseBase[] = [
  { version: "1.1.1", date: "2026-05-28" },
  { version: "1.1.0", date: "2026-05-20", postSlug: "introducing-cate-1-1" },
  { version: "1.0.2", date: "2026-03-25" },
  { version: "1.0.0", date: "2026-03-04", postSlug: "why-an-infinite-canvas" },
  { version: "0.9.3", date: "2026-02-10" },
  { version: "0.9.0", date: "2026-01-20" },
  { version: "0.7.0", date: "2025-12-02", postSlug: "fourteen-themes" },
  { version: "0.4.0", date: "2025-10-15" },
  { version: "0.1.0", date: "2025-09-01" },
];

/** Merge a base with its translation for `locale`. */
function resolve(base: ReleaseBase, locale: Locale): Release {
  const loc = dictionaries[locale].releases[base.version];
  return { ...base, title: loc?.title ?? "", summary: loc?.summary ?? "", highlights: loc?.highlights };
}

/** All releases for `locale`, newest first. */
export function getReleases(locale: Locale): Release[] {
  return releaseBases.map((b) => resolve(b, locale));
}

/** Look up a release by its version string in `locale`. */
export function getRelease(locale: Locale, version: string): Release | undefined {
  const base = releaseBases.find((r) => r.version === version);
  return base ? resolve(base, locale) : undefined;
}
